/** Ключи прав для ролей (JSON-массив в коллекции roles). */
export const PERMISSION_KEYS = {
  USERS_READ: 'users.read',
  USERS_WRITE: 'users.write',
  USERS_DELETE: 'users.delete',
  ROLES_READ: 'roles.read',
  ROLES_WRITE: 'roles.write',
  ROLES_DELETE: 'roles.delete',
  CONSUMABLES_READ: 'consumables.read',
  CONSUMABLES_WRITE: 'consumables.write',
  CONSUMABLES_DELETE: 'consumables.delete',
  PRODUCTS_READ: 'products.read',
  PRODUCTS_WRITE: 'products.write',
  PRODUCTS_DELETE: 'products.delete',
  PRODUCTION_REQUESTS_READ: 'production_requests.read',
  PRODUCTION_REQUESTS_WRITE: 'production_requests.write',
  PRODUCTION_REQUESTS_DELETE: 'production_requests.delete',
  MACHINES_READ: 'machines.read',
  MACHINES_WRITE: 'machines.write',
  MACHINES_DELETE: 'machines.delete',
  /** Фаза 1: смены производства. */
  SHIFTS_READ: 'shifts.read',
  SHIFTS_WRITE: 'shifts.write',
  /** Фаза 1: справочник производственных сотрудников. */
  PRODUCTION_WORKERS_READ: 'production_workers.read',
  PRODUCTION_WORKERS_WRITE: 'production_workers.write',
  PRODUCTION_WORKERS_DELETE: 'production_workers.delete',
  /** Фаза 1: график производства (слоты Gantt). */
  PRODUCTION_SCHEDULE_READ: 'production_schedule.read',
  PRODUCTION_SCHEDULE_WRITE: 'production_schedule.write',
  PRODUCTION_SCHEDULE_DELETE: 'production_schedule.delete',
  /** Фаза 1: просмотр графика всех смен (не только assignedShift). */
  PRODUCTION_SCHEDULE_ALL_SHIFTS: 'production_schedule.all_shifts',
  /** Фаза 1: табель производственных сотрудников. */
  TIMESHEETS_READ: 'timesheets.read',
  TIMESHEETS_WRITE: 'timesheets.write',
  /** Фаза 2: отчёт MRP-lite (потребность в сырье). */
  MATERIALS_READ: 'materials.read',
  /** Фаза 2: простои станков. */
  MACHINE_EVENTS_READ: 'machine_events.read',
  MACHINE_EVENTS_WRITE: 'machine_events.write',
  /** Фаза 2: производственный календарь. */
  PRODUCTION_CALENDAR_READ: 'production_calendar.read',
  PRODUCTION_CALENDAR_WRITE: 'production_calendar.write',
  /** Фаза 2: экран смены / kiosk. */
  PLANNING_KIOSK: 'planning.kiosk',
  /** Фаза 3: склад сырья и движения. */
  INVENTORY_READ: 'inventory.read',
  INVENTORY_WRITE: 'inventory.write',
  /** Фаза 3: склад готовой продукции. */
  FINISHED_GOODS_READ: 'finished_goods.read',
  FINISHED_GOODS_WRITE: 'finished_goods.write',
  /** Фаза 3: отгрузки. */
  SHIPMENTS_READ: 'shipments.read',
  SHIPMENTS_WRITE: 'shipments.write',
  SYSTEM_ADMIN: 'system.admin',
} as const

export type PermissionKey = (typeof PERMISSION_KEYS)[keyof typeof PERMISSION_KEYS]

/** Русские названия прав для UI админки. */
export const PERMISSION_LABELS: Record<string, string> = {
  [PERMISSION_KEYS.USERS_READ]: 'Просмотр пользователей',
  [PERMISSION_KEYS.USERS_WRITE]: 'Редактирование пользователей',
  [PERMISSION_KEYS.USERS_DELETE]: 'Удаление пользователей',
  [PERMISSION_KEYS.ROLES_READ]: 'Просмотр ролей',
  [PERMISSION_KEYS.ROLES_WRITE]: 'Редактирование ролей',
  [PERMISSION_KEYS.ROLES_DELETE]: 'Удаление ролей',
  [PERMISSION_KEYS.CONSUMABLES_READ]: 'Просмотр расходников (комплектация)',
  [PERMISSION_KEYS.CONSUMABLES_WRITE]: 'Редактирование расходников',
  [PERMISSION_KEYS.CONSUMABLES_DELETE]: 'Удаление расходников',
  [PERMISSION_KEYS.PRODUCTS_READ]: 'Просмотр продукции',
  [PERMISSION_KEYS.PRODUCTS_WRITE]: 'Редактирование продукции',
  [PERMISSION_KEYS.PRODUCTS_DELETE]: 'Удаление продукции',
  [PERMISSION_KEYS.PRODUCTION_REQUESTS_READ]: 'Просмотр заявок на производство',
  [PERMISSION_KEYS.PRODUCTION_REQUESTS_WRITE]: 'Редактирование заявок на производство',
  [PERMISSION_KEYS.PRODUCTION_REQUESTS_DELETE]: 'Удаление заявок на производство',
  [PERMISSION_KEYS.MACHINES_READ]: 'Просмотр станков',
  [PERMISSION_KEYS.MACHINES_WRITE]: 'Редактирование станков',
  [PERMISSION_KEYS.MACHINES_DELETE]: 'Удаление станков',
  [PERMISSION_KEYS.SHIFTS_READ]: 'Просмотр смен',
  [PERMISSION_KEYS.SHIFTS_WRITE]: 'Редактирование смен',
  [PERMISSION_KEYS.PRODUCTION_WORKERS_READ]: 'Просмотр сотрудников производства',
  [PERMISSION_KEYS.PRODUCTION_WORKERS_WRITE]: 'Редактирование сотрудников производства',
  [PERMISSION_KEYS.PRODUCTION_WORKERS_DELETE]: 'Удаление сотрудников производства',
  [PERMISSION_KEYS.PRODUCTION_SCHEDULE_READ]: 'Просмотр графика производства',
  [PERMISSION_KEYS.PRODUCTION_SCHEDULE_WRITE]: 'Редактирование графика производства',
  [PERMISSION_KEYS.PRODUCTION_SCHEDULE_DELETE]: 'Удаление слотов графика',
  [PERMISSION_KEYS.PRODUCTION_SCHEDULE_ALL_SHIFTS]: 'График всех смен (не только своя)',
  [PERMISSION_KEYS.TIMESHEETS_READ]: 'Просмотр табеля',
  [PERMISSION_KEYS.TIMESHEETS_WRITE]: 'Редактирование табеля',
  [PERMISSION_KEYS.MATERIALS_READ]: 'Просмотр потребности в материалах (MRP)',
  [PERMISSION_KEYS.MACHINE_EVENTS_READ]: 'Просмотр простоев станков',
  [PERMISSION_KEYS.MACHINE_EVENTS_WRITE]: 'Регистрация простоев станков',
  [PERMISSION_KEYS.PRODUCTION_CALENDAR_READ]: 'Просмотр производственного календаря',
  [PERMISSION_KEYS.PRODUCTION_CALENDAR_WRITE]: 'Редактирование производственного календаря',
  [PERMISSION_KEYS.PLANNING_KIOSK]: 'Экран смены (kiosk)',
  [PERMISSION_KEYS.INVENTORY_READ]: 'Просмотр склада сырья',
  [PERMISSION_KEYS.INVENTORY_WRITE]: 'Операции по складу сырья',
  [PERMISSION_KEYS.FINISHED_GOODS_READ]: 'Просмотр склада готовой продукции',
  [PERMISSION_KEYS.FINISHED_GOODS_WRITE]: 'Операции по складу готовой продукции',
  [PERMISSION_KEYS.SHIPMENTS_READ]: 'Просмотр отгрузок',
  [PERMISSION_KEYS.SHIPMENTS_WRITE]: 'Редактирование отгрузок',
  [PERMISSION_KEYS.SYSTEM_ADMIN]: 'Системное администрирование',
  '*': 'Все права (супердоступ)',
}

export function permissionLabel(key: string): string {
  return PERMISSION_LABELS[key] ?? key
}

export function formatPermissionsDisplay(raw: unknown): string {
  const list = normalizePermissions(raw)
  if (list.length === 0) return ''
  return list.map(permissionLabel).join(', ')
}

export const PERMISSION_GROUPS: {
  label: string
  keys: readonly PermissionKey[]
}[] = [
  {
    label: 'Пользователи',
    keys: [
      PERMISSION_KEYS.USERS_READ,
      PERMISSION_KEYS.USERS_WRITE,
      PERMISSION_KEYS.USERS_DELETE,
    ],
  },
  {
    label: 'Роли и права',
    keys: [
      PERMISSION_KEYS.ROLES_READ,
      PERMISSION_KEYS.ROLES_WRITE,
      PERMISSION_KEYS.ROLES_DELETE,
    ],
  },
  {
    label: 'Комплектация (расходники)',
    keys: [
      PERMISSION_KEYS.CONSUMABLES_READ,
      PERMISSION_KEYS.CONSUMABLES_WRITE,
      PERMISSION_KEYS.CONSUMABLES_DELETE,
    ],
  },
  {
    label: 'Продукция',
    keys: [
      PERMISSION_KEYS.PRODUCTS_READ,
      PERMISSION_KEYS.PRODUCTS_WRITE,
      PERMISSION_KEYS.PRODUCTS_DELETE,
    ],
  },
  {
    label: 'Заявки на производство',
    keys: [
      PERMISSION_KEYS.PRODUCTION_REQUESTS_READ,
      PERMISSION_KEYS.PRODUCTION_REQUESTS_WRITE,
      PERMISSION_KEYS.PRODUCTION_REQUESTS_DELETE,
    ],
  },
  {
    label: 'Станки',
    keys: [
      PERMISSION_KEYS.MACHINES_READ,
      PERMISSION_KEYS.MACHINES_WRITE,
      PERMISSION_KEYS.MACHINES_DELETE,
    ],
  },
  {
    label: 'Смены',
    keys: [PERMISSION_KEYS.SHIFTS_READ, PERMISSION_KEYS.SHIFTS_WRITE],
  },
  {
    label: 'Сотрудники производства',
    keys: [
      PERMISSION_KEYS.PRODUCTION_WORKERS_READ,
      PERMISSION_KEYS.PRODUCTION_WORKERS_WRITE,
      PERMISSION_KEYS.PRODUCTION_WORKERS_DELETE,
    ],
  },
  {
    label: 'График производства',
    keys: [
      PERMISSION_KEYS.PRODUCTION_SCHEDULE_READ,
      PERMISSION_KEYS.PRODUCTION_SCHEDULE_WRITE,
      PERMISSION_KEYS.PRODUCTION_SCHEDULE_DELETE,
      PERMISSION_KEYS.PRODUCTION_SCHEDULE_ALL_SHIFTS,
    ],
  },
  {
    label: 'Табель',
    keys: [PERMISSION_KEYS.TIMESHEETS_READ, PERMISSION_KEYS.TIMESHEETS_WRITE],
  },
  {
    label: 'Материалы (MRP)',
    keys: [PERMISSION_KEYS.MATERIALS_READ],
  },
  {
    label: 'Простои станков',
    keys: [PERMISSION_KEYS.MACHINE_EVENTS_READ, PERMISSION_KEYS.MACHINE_EVENTS_WRITE],
  },
  {
    label: 'Производственный календарь',
    keys: [
      PERMISSION_KEYS.PRODUCTION_CALENDAR_READ,
      PERMISSION_KEYS.PRODUCTION_CALENDAR_WRITE,
    ],
  },
  {
    label: 'Планирование (kiosk)',
    keys: [PERMISSION_KEYS.PLANNING_KIOSK],
  },
  {
    label: 'Склад сырья',
    keys: [PERMISSION_KEYS.INVENTORY_READ, PERMISSION_KEYS.INVENTORY_WRITE],
  },
  {
    label: 'Склад готовой продукции',
    keys: [PERMISSION_KEYS.FINISHED_GOODS_READ, PERMISSION_KEYS.FINISHED_GOODS_WRITE],
  },
  {
    label: 'Отгрузки',
    keys: [PERMISSION_KEYS.SHIPMENTS_READ, PERMISSION_KEYS.SHIPMENTS_WRITE],
  },
  {
    label: 'Система',
    keys: [PERMISSION_KEYS.SYSTEM_ADMIN],
  },
]

export function normalizePermissions(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === 'string')
  }
  if (raw && typeof raw === 'object' && Array.isArray((raw as { permissions?: unknown }).permissions)) {
    return normalizePermissions((raw as { permissions: unknown }).permissions)
  }
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown
      return normalizePermissions(p)
    } catch {
      return []
    }
  }
  return []
}
